import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { router } from '@inertiajs/react'
import {
  Calendar,
  Lock,
  Users,
  Package,
  ShoppingBag,
  UserCheck,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface LessonToday {
  id: number
  start_time?: string | null
  end_time?: string | null
  status: string
  teacher_name?: string | null
  student_name?: string | null
}

interface Props {
  school: { id: number; name: string }
  lessons_today: LessonToday[]
  stats: { lessons_today_total: number; lessons_today_booked: number }
  modules: string[]
}

// Os atalhos existem porque o menu lateral de um gerente com poucos módulos é curto
// demais para servir de orientação: ele abre o painel e precisa ver o que pode fazer.
const SHORTCUTS: {
  module: string
  label: string
  url: string
  icon: LucideIcon
}[] = [
  { module: 'agenda', label: 'Agenda', url: '/lessons', icon: Calendar },
  { module: 'alunos', label: 'Alunos', url: '/students', icon: Users },
  {
    module: 'assinaturas',
    label: 'Assinaturas',
    url: '/student_subscriptions',
    icon: UserCheck,
  },
  {
    module: 'pacotes',
    label: 'Pacotes',
    url: '/lesson_packages',
    icon: Package,
  },
  { module: 'balcao', label: 'Balcão', url: '/sales', icon: ShoppingBag },
]

export default function ManagerDashboard({
  school,
  lessons_today,
  stats,
  modules,
}: Props) {
  const shortcuts = SHORTCUTS.filter((item) => modules.includes(item.module))

  // Um gerente criado sem nenhum módulo cai aqui. Sem esta tela ele veria um painel
  // vazio e não saberia se está quebrado ou se falta liberar — e abriria chamado.
  if (modules.length === 0) {
    return (
      <DashboardLayout schoolName={school.name}>
        <div className="p-6">
          <Card className="max-w-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-muted p-2">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>
                <CardTitle>Seu acesso ainda não foi liberado</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Sua conta existe, mas nenhum módulo foi liberado ainda. Fale com o
              dono da escola para escolher a que você terá acesso.
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Olá!</h2>
          <p className="text-sm text-muted-foreground">
            O que está acontecendo hoje
          </p>
        </div>

        {shortcuts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {shortcuts.map((item) => {
              const Icon = item.icon
              return (
                <Button
                  key={item.module}
                  variant="outline"
                  onClick={() => router.visit(item.url)}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              )
            })}
          </div>
        )}

        {modules.includes('agenda') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Aulas de hoje
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {stats.lessons_today_booked} de {stats.lessons_today_total}{' '}
                  com aluno
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lessons_today.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma aula marcada para hoje.
                </p>
              ) : (
                <ul className="divide-y">
                  {lessons_today.map((lesson) => (
                    <li
                      key={lesson.id}
                      className="flex items-center gap-3 py-2 text-sm"
                    >
                      <span className="font-medium tabular-nums w-24">
                        {lesson.start_time}
                        {lesson.end_time ? `–${lesson.end_time}` : ''}
                      </span>
                      <span className="flex-1">
                        {lesson.student_name || (
                          <span className="text-muted-foreground">
                            Vaga livre
                          </span>
                        )}
                      </span>
                      {lesson.teacher_name && (
                        <span className="text-muted-foreground">
                          {lesson.teacher_name}
                        </span>
                      )}
                      <Badge variant="outline">{lesson.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}
